<template>
  <ModalDialog
    ref="dialog"
    title="Edit Task"
    :submitting="submitting"
    submitTitle="Update"
    icon="fa-calendar-edit"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <FormGroup v-bind:form="dialogForm" :labelColumnSize="3" v-if="!loading && !loadingError">
      <FormRow label="Name">
        <FormInputText v-model="task.name" placeholder="Name" field="name" />
      </FormRow>
      <FormRow label="Description">
        <FormInputTextArea v-model="task.description" placeholder="Description" field="description" />
      </FormRow>
      <FormRow label="Start Date">
        <FormDateTime v-model="task.scheduleStartDate" field="scheduleStartDate" />
      </FormRow>
      <FormRow label="Labels">
        <FormLabelTag popupName="Task Labels" v-model="task.labels" labelType="task" field="labelList" />
      </FormRow>

      <div v-if="isLocationTrackingEnabled">
        <FormHorizontalRule />
        <FormRow label="Route Start Location" :fieldCount="2">
          <template v-slot:field1>
            <FormSelect v-model="task.routeStartLocationType" :options="TaskRouteLocationTypeLookup" field="routeStartLocationType" note="Select the location type for the start of the route" />
          </template>
          <template v-slot:field2>
            <FormInputAddressSearch v-show="task.routeStartLocationType == TaskRouteLocationTypes.address" v-model="task.routeStartLocationAddress" :splitAddressComponents="false" placeholder="Address" field="routeStartLocationAddress" />
            <FormSelect v-show="task.routeStartLocationType == TaskRouteLocationTypes.accountLocation" v-model="task.startPointAccountLocationId" :options="accountLocationLookup" field="startPointAccountLocationId" />
          </template>
        </FormRow>

        <FormRow label="Route End Location" :fieldCount="2">
          <template v-slot:field1>
            <FormSelect v-model="task.routeEndLocationType" :options="TaskRouteLocationTypeLookup" field="routeEndLocationType"  note="Select the location type for the end of the route after completing the items" />
          </template>
          <template v-slot:field2>
            <FormInputAddressSearch v-show="task.routeEndLocationType == TaskRouteLocationTypes.address" v-model="task.routeEndLocationAddress" :splitAddressComponents="false" placeholder="Address" field="routeEndLocationAddress" />
            <FormSelect v-show="task.routeEndLocationType == TaskRouteLocationTypes.accountLocation" v-model="task.endPointAccountLocationId" :options="accountLocationLookup" field="endPointAccountLocationId" />
          </template>
        </FormRow>
      </div>

      <FormHorizontalRule />
      <FormRow label="Primary User" helpTip="The primary user is the main user assigned to the task" v-if="isPrimaryUserRequired">
        <FormSelect :options="userList" v-model="task.primaryUserId" placeholder="Primary User" field="primaryUserId" />
      </FormRow>
      <FormRow label="Assigned Users">
        <FormMultiSelect :options="userList" v-model="selectedUsers" placeholder="Users" field="users" />
      </FormRow>
      <FormRow label="Assigned Resources">
        <FormMultiSelect :options="resourceList" v-model="selectedResources" placeholder="Resources" field="resources" />
      </FormRow>

      <FormHorizontalRule />
      <FormRow label="Alerts" helpTip="Requires at least 1 user resource assigned to the task to send alerts">
        <FormStaticLabel>
          <TaskAlertList v-model="task.taskAlerts" :editing="true" />
        </FormStaticLabel>
      </FormRow>      
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'

import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import TaskAlertList from '@/modules/tasks/components/taskAlertList'

import { ResourceTypes, TaskRouteLocationTypeLookup, TaskRouteLocationTypes } from '@/store/types'

export default {
  name: 'TaskEditDialog',
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    TaskAlertList
  },
  props: {
    taskId: Number
  },
  data() {
    return {
      responseInfo: {},
      dialogForm: {},

      submitting: false,
      loading: false,
      loadingError: '',

      userList: [],
      resourceList: [],
      selectedUsers: [],
      selectedResources: [],
      accountLocationList: [],

      task: {
        labels: []
      }
    }
  },
  watch: {
  },
  created() {
    this.TaskRouteLocationTypeLookup = TaskRouteLocationTypeLookup
    this.TaskRouteLocationTypes = TaskRouteLocationTypes
  },
  mounted() {
    
  },
  computed: {
    isLocationTrackingEnabled() {
      if(this.task && this.task.taskType)
        return this.task.taskType.isLocationTrackingEnabled
      return false
    },
    isPrimaryUserRequired() {
      if(this.task && this.task.taskType)
        return this.task.taskType.isPrimaryUserRequired
      return false
    },
    accountLocationLookup() {
      return _.concat([{ name: 'None', value: null }],
        this.accountLocationList.map(location => {
          return { name: `${location.name} - ${location.formattedAddress}`, value: location.id }
        })
      )
    },
  },
  methods: {
    show(task) {
      // Reset form
      this.responseInfo = { }
      this.dialogForm = { }

      this.requestData()

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    requestData() {
      this.loading = true
      this.loadingError = ''

      Promise.all([
        this.$apiService.tasksService.getTask(this.taskId),
        this.$apiService.tasksService.getResourceList(),
        this.$apiService.userService.getUserList(),
        this.$apiService.adminService.getAccountLocations(),
      ])
      .then(results => {
        this.task = results[0].data
        this.accountLocationList = results[3].data

        // Split up the resources into 2 fields
        this.selectedUsers = this.task.users.map(user => user.id)
        this.selectedResources = this.task.resources.filter(resource => resource.type == ResourceTypes.general).map(resource => resource.id)

        this.resourceList = results[1].data
        .filter(resource => resource.isActive && resource.type == ResourceTypes.general)
        .map(resource => {
          return {
            value: resource.id,
            name: resource.name,
            colour: resource.highlightColour
          }
        })
        this.userList = results[2].data
        .filter(user => user.isActive && user.isTaskResource)
        .map(user => {
          return {
            value: user.id,
            name: user.name,
            colour: user.highlightColour
          }
        })

      })
      .catch(err => {
        this.loadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loading = false
      })
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.submitting = true

      var updateData = _.pick(this.task, ['name', 'description', 'scheduleStartDate', 'taskAlerts'])
      updateData.scheduleEndDate = updateData.scheduleStartDate

      if(this.isPrimaryUserRequired)
        updateData.primaryUserId = this.task.primaryUserId

      if(this.isLocationTrackingEnabled) {
        updateData = _.merge(updateData, _.pick(this.task, ['routeStartLocationType', 'routeEndLocationType']))

        if(this.task.routeStartLocationType == TaskRouteLocationTypes.accountLocation)
          updateData.startPointAccountLocationId = this.task.startPointAccountLocationId
        if(this.task.routeEndLocationType == TaskRouteLocationTypes.accountLocation)
          updateData.endPointAccountLocationId = this.task.endPointAccountLocationId

        if(this.task.routeStartLocationType == TaskRouteLocationTypes.address)
          updateData.routeStartLocationAddress = this.task.routeStartLocationAddress
        if(this.task.routeEndLocationType == TaskRouteLocationTypes.address)
          updateData.routeEndLocationAddress = this.task.routeEndLocationAddress
      }

      updateData.resources = this.selectedResources
      updateData.users = this.selectedUsers
      updateData.labelList = this.task.labels.map(label => label.id)

      // Make sure the primary user is in the user list too if set
      if(this.task.primaryUserId && !updateData.users.includes(this.task.primaryUserId) && this.isPrimaryUserRequired) {
        updateData.users.push(this.task.primaryUserId)
      }

      this.$apiService.tasksService.updateTask(this.taskId, updateData)
      .then(response => {
        this.modalClosed(true)
        this.hide()
      })
      .catch(err => {
        this.$data.dialogForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.submitting = false
      })
    }
  }
}
</script>